import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import apiClient from "../../../business/network/ApiClient";
import SparkleIcon from "../Shared/SparkleIcon";
import ScheduleFilesWarning from "./ScheduleFilesWarning";

const PromptSelectionSchedule = ({ questions, onClose, onContinueSchedule, currentQuestion, uploadedFiles }) => {
    const [proposedQuestions, setProposedQuestions] = useState(null);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    // File uploaded without a prompt: no original prompt card to offer
    const hasOriginalPrompt = !!currentQuestion?.trim();

    useEffect(() => {
        if (questions && proposedQuestions === null) {
            const processSchedulableQuestions = async () => {
                const response = await apiClient.getSchedulableQuestions(questions);
                if (response?.data && response.data?.options && response.data.options.length > 0) {
                    let data = response.data?.options;
                    if (hasOriginalPrompt) {
                        data.unshift(currentQuestion);
                    } else {
                        setSelectedQuestion(0);
                        setSelectedPrompt(data[0]);
                    }
                    setProposedQuestions(data);
                }
            };
            processSchedulableQuestions();
        }
    }, [questions, proposedQuestions]);

    // Calculate grid columns based on number of questions
    const getGridCols = (count) => {
        if (count <= 2) return "grid-cols-1 md:grid-cols-2";
        if (count <= 3) return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
        return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
    };

    return (
        <>
            <div className="md:min-w-[770px]" style={{ minHeight: '500px' }}>
                <div className="text-xl text-dark-blue-100 font-bold py-4 px-6 border-b border-whitesmoke-100 flex items-center justify-between">
                    Which Scheduled Question Works Best?
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="hover:cursor-pointer"
                        onClick={onClose}
                    >
                        <path
                            d="M4 4L16 16M16 4L4 16"
                            stroke="#303C54"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                </div>

                {proposedQuestions !== null && proposedQuestions.length > 0 && (
                    <div className="space-y-7 flex flex-col p-6">
                        <ScheduleFilesWarning files={uploadedFiles} />
                        <div className="flex flex-col gap-1.5">
                            <p className="font-semibold text-dark-blue-100 pb-5 text-sm">
                                We've suggested a few versions based on your chat. Pick the one that matches your intent most closely.
                            </p>

                            {/* Dynamic grid that adjusts to number of cards */}
                            <div className="w-full">
                                <div
                                    className={`grid gap-3 ${getGridCols(proposedQuestions.length)} justify-items-center`}
                                    style={{ gridAutoRows: 'max-content' }}
                                >
                                    {proposedQuestions.map((value, index) => (
                                        <div
                                            key={index}
                                            onClick={() => {
                                                setSelectedQuestion(index);
                                                setSelectedPrompt(value);
                                            }}
                                            className={`relative cursor-pointer transition-all w-full flex flex-col ${selectedQuestion === index
                                                ? 'bg-purple-50 border-purple-400 border-2'
                                                : 'bg-white border-2 border-purple-200 hover:bg-purple-50 hover:border-purple-400'
                                                }`}
                                            style={{
                                                maxWidth: '290px',
                                                minHeight: '220px',
                                                borderRadius: '20px',
                                                padding: '20px'
                                            }}
                                        >
                                            {/* Logo - positioned centered at top for AI-suggested cards */}
                                            {!(hasOriginalPrompt && index === 0) && (
                                                <div className="absolute top-3 left-1/2 transform -translate-x-1/2">
                                                    <div className="w-8 h-8 rounded-full flex items-center justify-center">
                                                        <SparkleIcon className="text-white" size={16} />
                                                    </div>
                                                </div>
                                            )}

                                            {(hasOriginalPrompt && index === 0) && (
                                                <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
                                                    <div className="flex items-center justify-center"
                                                        style={{ color: '#B0AEE0', whiteSpace: 'nowrap', fontSize: '10px', fontWeight: '600', fontSize: '16px' }}>
                                                        Original Prompt
                                                    </div>
                                                </div>
                                            )}

                                            {/* Question Text Container */}
                                            <div className={`text-gray-700 text-base leading-relaxed flex items-center h-full  text-sm ${hasOriginalPrompt && index === 0 ? 'justify-center' : 'pt-12'}`}>
                                                <div
                                                    className="w-full text-center custom-scrollbar"
                                                    style={{
                                                        maxHeight: '230px',
                                                        overflowY: 'auto',
                                                        scrollbarWidth: 'thin',
                                                        scrollbarColor: '#C5C3F1 #f3f4f6'
                                                    }}>
                                                    {value}
                                                </div>
                                                <style jsx>{`
                                                    .custom-scrollbar::-webkit-scrollbar {
                                                        width: 6px;
                                                    }
                                                    .custom-scrollbar::-webkit-scrollbar-track {
                                                        background: #f3f4f6;
                                                    }
                                                    .custom-scrollbar::-webkit-scrollbar-thumb {
                                                        background: #C5C3F1;
                                                        border-radius: 10px;
                                                    }
                                                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                                                        background: #B0ADE8;
                                                    }
                                                    .custom-scrollbar::-webkit-scrollbar-button:single-button {
                                                        display: none;
                                                        height: 0;
                                                        width: 0;
                                                    }
                                                    .custom-scrollbar::-webkit-scrollbar-button:vertical:start:decrement,
                                                    .custom-scrollbar::-webkit-scrollbar-button:vertical:end:increment {
                                                        display: none;
                                                    }
                                                `}</style>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={onClose}
                                className="border rounded-2xl py-3 w-[92px] border-dimgray-200 text-dark-blue-100 font-semibold leading-4.5"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => onContinueSchedule(selectedPrompt)}
                                className={selectedQuestion === null
                                    ? "border rounded-2xl py-3 w-[92px] bg-custom-gray text-white font-bold leading-4.5"
                                    : "border rounded-2xl py-3 w-[92px] border-dimgray-200 bg-dark-blue-100 text-white font-bold leading-4.5"
                                }
                                disabled={selectedQuestion === null}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {proposedQuestions === null && (
                    <div className="flex items-center justify-center pb-20 h-full" style={{ minHeight: '440px' }}>
                        <div className="relative w-[150px] h-[150px] rounded-full flex items-center justify-center">
                            <img
                                src="/images/animation.svg"
                                alt="Mila thinking..."
                                className="absolute inset-0 w-full h-full object-cover animate-circle-spin"
                            />
                            <Sparkles
                                className="relative z-10"
                                style={{ color: '#C5C3F1' }}
                                size={52}
                            />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default PromptSelectionSchedule;